import React, { useState, useEffect } from "react";
import Brand from "@/components/Brand";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Buildings, MapPin, CheckCircle, Sparkle, ArrowRight, Package } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GlassCard } from "@/components/glass/GlassCard";
import StarterPackPicker from "@/components/StarterPackPicker";
import api from "@/lib/api";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1765164478579-2c041dad2c7d?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [org, setOrg] = useState("");
  const [industry, setIndustry] = useState("general");
  const [selectedRegions, setSelectedRegions] = useState(["global"]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/compliance/frameworks").then(({ data }) => {
      setIndustries(data.industries); setRegions(data.regions);
    });
  }, []);

  const toggleRegion = (id) => setSelectedRegions(selectedRegions.includes(id) ? selectedRegions.filter((x) => x !== id) : [...selectedRegions, id]);

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/industry", { organization: org, industry, regions: selectedRegions });
      setStep(3);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  const enterApp = () => {
    toast.success("Welcome to Clariva");
    nav("/app");
  };

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 -z-10">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg)]/40 to-[var(--bg)]" />
      </div>
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-8">
          <Brand size={36} />
          <div>
            <div className="overline text-[var(--accent)]">Welcome to Clariva</div>
            <div className="text-sm text-[var(--text-2)]">Tell us about your workspace in 3 quick steps.</div>
          </div>
        </div>

        <div className="flex gap-2 mb-8">
          {[0, 1, 2, 3].map((s) => (
            <div key={s} className={`flex-1 h-1 rounded-full ${step >= s ? "bg-[var(--accent)]" : "bg-[var(--border)]"}`} />
          ))}
        </div>

        {step === 0 && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <GlassCard strong className="p-8">
              <div className="overline mb-1">Step 1 of 4</div>
              <h1 className="text-3xl font-medium mb-6" style={{ fontFamily: 'var(--font-heading)' }}>What's your organization?</h1>
              <Input autoFocus value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Acme Corp" data-testid="onboarding-org-input" className="h-12 glass-subtle border-[var(--border-strong)] text-lg mb-6" />
              <Button onClick={() => setStep(1)} disabled={!org} data-testid="onboarding-next-1" size="lg" className="gap-2">Continue <ArrowRight size={14} /></Button>
            </GlassCard>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <GlassCard strong className="p-8">
              <div className="overline mb-1">Step 2 of 4</div>
              <h1 className="text-3xl font-medium mb-6 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                <Buildings size={26} weight="duotone" className="text-[var(--accent)]" />
                What industry are you in?
              </h1>
              <p className="text-sm text-[var(--text-2)] mb-6">We'll tune templates, compliance frameworks, and AI clause libraries to your vertical.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                {industries.map((ind) => (
                  <button key={ind.id} onClick={() => setIndustry(ind.id)} data-testid={`onboarding-industry-${ind.id}`} className={`text-left p-4 rounded-lg border transition ${industry === ind.id ? "border-[var(--accent)] bg-[var(--surface-2)]" : "border-[var(--border)] hover:border-[var(--border-strong)]"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{ind.name}</span>
                      {industry === ind.id && <CheckCircle size={14} weight="fill" className="text-emerald-400" />}
                    </div>
                    <div className="text-[10px] text-[var(--text-3)] font-mono mt-1">{ind.default_compliance.join(", ").toUpperCase()}</div>
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setStep(0)} data-testid="onboarding-back-1">Back</Button>
                <Button onClick={() => setStep(2)} data-testid="onboarding-next-2" size="lg" className="gap-2 ms-auto">Continue <ArrowRight size={14} /></Button>
              </div>
            </GlassCard>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <GlassCard strong className="p-8">
              <div className="overline mb-1">Step 3 of 4</div>
              <h1 className="text-3xl font-medium mb-6 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                <MapPin size={26} weight="duotone" className="text-[var(--accent)]" />
                Where do you operate?
              </h1>
              <p className="text-sm text-[var(--text-2)] mb-6">Select all regions — we'll apply the right compliance rules automatically.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                {regions.map((r) => {
                  const on = selectedRegions.includes(r.id);
                  return (
                    <button key={r.id} onClick={() => toggleRegion(r.id)} data-testid={`onboarding-region-${r.id}`} className={`text-left p-4 rounded-lg border transition ${on ? "bg-[var(--surface-strong)] border-[var(--accent)]" : "bg-[var(--surface)] border-[var(--border)] hover:border-[var(--border-strong)]"}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{r.name}</span>
                        {on && <CheckCircle size={14} weight="fill" className="text-emerald-400" />}
                      </div>
                      <div className="text-[10px] text-[var(--text-3)] font-mono mt-1">{r.compliance.join(", ").toUpperCase()}</div>
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setStep(1)} data-testid="onboarding-back-2">Back</Button>
                <Button onClick={finish} disabled={loading || selectedRegions.length === 0} data-testid="onboarding-finish" size="lg" className="gap-2 ms-auto">{loading ? "Setting up…" : "Continue"} <ArrowRight size={14} /></Button>
              </div>
            </GlassCard>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <GlassCard strong className="p-8">
              <div className="overline mb-1">Step 4 of 4 · Optional</div>
              <h1 className="text-3xl font-medium mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                <Package size={26} weight="duotone" className="text-[var(--accent)]" />
                Ship with a starter pack?
              </h1>
              <p className="text-sm text-[var(--text-2)] mb-6">
                Each pack installs industry-tuned boilerplate contracts, document-style presets (letterhead, watermark), approval workflows, and the mappings that wire them together — so your tenant is production-ready in under a minute.
              </p>
              <StarterPackPicker
                compact
                allowSkip
                onSkip={enterApp}
                onImported={() => setTimeout(enterApp, 700)}
              />
              <div className="mt-6 text-[11px] text-[var(--text-3)]">
                You can import any pack later from <code className="font-mono text-[var(--accent)]">Admin → Organisations → Starter packs</code>.
              </div>
            </GlassCard>
          </motion.div>
        )}
      </div>
    </div>
  );
}
